import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Dialog, DialogModule, DialogRef } from '@angular/cdk/dialog';
import { ButtonComponent } from '@akostm/orbit-ui';
import { ColorDialogComponent, ColorSelection } from './color-dialog/color-dialog.component';
import { GeneralService } from '../general/general.service';

@Component({
  selector: 'lib-settings-theme',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonComponent,
  ],
  templateUrl: './theme.component.html',
  styleUrls: ['./theme.component.scss'],
})
export class ThemeComponent {
  readonly #dialog = inject(Dialog);
  readonly #translateService = inject(TranslateService);
  readonly #fb = inject(FormBuilder);
  readonly #generalService = inject(GeneralService);

  selectedHeaderColor = signal(this.getCSSVariable('--background-color'));
  selectedButtonColor = signal(this.getCSSVariable('--button-primary-bg'));
  selectedButtonLabelColor = signal(this.getCSSVariable('--button-label-color'));
  selectedButtonHoverColor = signal(this.getCSSVariable('--button-primary-bg-hover'));
  selectedClientButtonColor = signal(this.getCSSVariable('--client-button-primary-bg'));
  selectedClientButtonHoverColor = signal(this.getCSSVariable('--client-button-primary-bg-hover'));
  selectedClientButtonLabelColor = signal(this.getCSSVariable('--client-button-label-color'));
  selectedSenderChatBubbleColor = signal(this.getCSSVariable('--sender-chat-bubble-color'));
  selectedParticipantChatBubbleColor = signal(
    this.getCSSVariable('--participant-chat-bubble-color')
  );
  selectedSenderChatBubbleTextColor = signal(
    this.getCSSVariable('--sender-chat-bubble-text-color')
  );
  selectedParticipantChatBubbleTextColor = signal(
    this.getCSSVariable('--participant-chat-bubble-text-color')
  );
  selectedClientSenderChatBubbleColor = signal(
    this.getCSSVariable('--client-sender-chat-bubble-color')
  );
  selectedClientParticipantChatBubbleColor = signal(
    this.getCSSVariable('--client-participant-chat-bubble-color')
  );
  selectedClientSenderChatBubbleTextColor = signal(
    this.getCSSVariable('--client-sender-chat-bubble-text-color')
  );
  selectedClientParticipantChatBubbleTextColor = signal(
    this.getCSSVariable('--client-participant-chat-bubble-text-color')
  );
  readonly editIcon = faEdit;

  getCSSVariable(variable: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  }

  openColorPickerDialog(): void {
    const headerColor = this.selectedHeaderColor();
    const buttonColor = this.selectedButtonColor();
    const buttonHoverColor = this.selectedButtonHoverColor();
    const buttonLabelColor = this.selectedButtonLabelColor();
    const clientButtonColor = this.selectedClientButtonColor();
    const clientButtonHoverColor = this.selectedClientButtonHoverColor();
    const clientButtonLabelColor = this.selectedClientButtonLabelColor();
    const senderChatBubbleColor = this.selectedSenderChatBubbleColor();
    const participantChatBubbleColor = this.selectedParticipantChatBubbleColor();
    const senderChatBubbleTextColor = this.selectedSenderChatBubbleTextColor();
    const participantChatBubbleTextColor = this.selectedParticipantChatBubbleTextColor();
    const clientSenderChatBubbleColor = this.selectedClientSenderChatBubbleColor();
    const clientParticipantChatBubbleColor = this.selectedClientParticipantChatBubbleColor();
    const clientSenderChatBubbleTextColor = this.selectedClientSenderChatBubbleTextColor();
    const clientParticipantChatBubbleTextColor =
      this.selectedClientParticipantChatBubbleTextColor();

    const colorForm = this.#fb.group({
      headerColor: [headerColor],
      buttonColor: [buttonColor],
      buttonHoverColor: [buttonHoverColor],
      buttonLabelColor: [buttonLabelColor],
      clientButtonColor: [clientButtonColor],
      clientButtonHoverColor: [clientButtonHoverColor],
      clientButtonLabelColor: [clientButtonLabelColor],
      senderChatBubbleColor: [senderChatBubbleColor],
      participantChatBubbleColor: [participantChatBubbleColor],
      senderChatBubbleTextColor: [senderChatBubbleTextColor],
      participantChatBubbleTextColor: [participantChatBubbleTextColor],
      clientSenderChatBubbleColor: [clientSenderChatBubbleColor],
      clientParticipantChatBubbleColor: [clientParticipantChatBubbleColor],
      clientSenderChatBubbleTextColor: [clientSenderChatBubbleTextColor],
      clientParticipantChatBubbleTextColor: [clientParticipantChatBubbleTextColor],
    });

    const dialogRef: DialogRef<ColorSelection, ColorDialogComponent> = this.#dialog.open(
      ColorDialogComponent,
      {
        data: {
          form: colorForm,
          title: this.#translateService.instant('SETTINGS.THEME.COLORS.CHANGE_THEME_COLORS'),
          cancelLabel: this.#translateService.instant('SETTINGS.GENERAL.CANCEL'),
          confirmLabel: this.#translateService.instant('SETTINGS.GENERAL.SAVE'),
        },
        width: '32rem',
      }
    );

    dialogRef.closed.subscribe(async (result: ColorSelection | undefined) => {
      if (result) {
        if (result.headerColor) {
          this.#setColorVariable('--background-color', result.headerColor);
        }
        if (result.buttonColor) {
          this.#setColorVariable('--button-primary-bg', result.buttonColor);
        }
        if (result.buttonHoverColor) {
          this.#setColorVariable('--button-primary-bg-hover', result.buttonHoverColor);
        }
        if (result.buttonLabelColor) {
          this.#setColorVariable('--button-label-color', result.buttonLabelColor);
        }
        if (result.clientButtonColor) {
          this.#setColorVariable('--client-button-primary-bg', result.clientButtonColor);
        }
        if (result.clientButtonHoverColor) {
          this.#setColorVariable('--client-button-primary-bg-hover', result.clientButtonHoverColor);
        }
        if (result.clientButtonLabelColor) {
          this.#setColorVariable('--client-button-label-color', result.clientButtonLabelColor);
        }
        if (result.senderChatBubbleColor) {
          this.#setColorVariable('--sender-chat-bubble-color', result.senderChatBubbleColor);
        }
        if (result.participantChatBubbleColor) {
          this.#setColorVariable(
            '--participant-chat-bubble-color',
            result.participantChatBubbleColor
          );
        }
        if (result.senderChatBubbleTextColor) {
          this.#setColorVariable(
            '--sender-chat-bubble-text-color',
            result.senderChatBubbleTextColor
          );
        }
        if (result.participantChatBubbleTextColor) {
          this.#setColorVariable(
            '--participant-chat-bubble-text-color',
            result.participantChatBubbleTextColor
          );
        }
        if (result.clientSenderChatBubbleColor) {
          this.#setColorVariable(
            '--client-sender-chat-bubble-color',
            result.clientSenderChatBubbleColor
          );
        }
        if (result.clientParticipantChatBubbleColor) {
          this.#setColorVariable(
            '--client-participant-chat-bubble-color',
            result.clientParticipantChatBubbleColor
          );
        }
        if (result.clientSenderChatBubbleTextColor) {
          this.#setColorVariable(
            '--client-sender-chat-bubble-text-color',
            result.clientSenderChatBubbleTextColor
          );
        }
        if (result.clientParticipantChatBubbleTextColor) {
          this.#setColorVariable(
            '--client-participant-chat-bubble-text-color',
            result.clientParticipantChatBubbleTextColor
          );
        }

        await this.#generalService.updateTenantColorConfig({
          headerColor: result.headerColor || null,
          buttonColor: result.buttonColor || null,
          buttonHoverColor: result.buttonHoverColor || null,
          buttonLabelColor: result.buttonLabelColor || null,
          clientButtonColor: result.clientButtonColor || null,
          clientButtonHoverColor: result.clientButtonHoverColor || null,
          clientButtonLabelColor: result.clientButtonLabelColor || null,
          senderChatBubbleColor: result.senderChatBubbleColor || null,
          participantChatBubbleColor: result.participantChatBubbleColor || null,
          senderChatBubbleTextColor: result.senderChatBubbleTextColor || null,
          participantChatBubbleTextColor: result.participantChatBubbleTextColor || null,
          clientSenderChatBubbleColor: result.clientSenderChatBubbleColor || null,
          clientParticipantChatBubbleColor: result.clientParticipantChatBubbleColor || null,
          clientSenderChatBubbleTextColor: result.clientSenderChatBubbleTextColor || null,
          clientParticipantChatBubbleTextColor: result.clientParticipantChatBubbleTextColor || null,
        });

        this.selectedHeaderColor.set(result.headerColor || '');
        this.selectedButtonColor.set(result.buttonColor || '');
        this.selectedButtonHoverColor.set(result.buttonHoverColor || '');
        this.selectedButtonLabelColor.set(result.buttonLabelColor || '');
        this.selectedClientButtonColor.set(result.clientButtonColor || '');
        this.selectedClientButtonHoverColor.set(result.clientButtonHoverColor || '');
        this.selectedClientButtonLabelColor.set(result.clientButtonLabelColor || '');
        this.selectedSenderChatBubbleColor.set(result.senderChatBubbleColor || '');
        this.selectedParticipantChatBubbleColor.set(result.participantChatBubbleColor || '');
        this.selectedSenderChatBubbleTextColor.set(result.senderChatBubbleTextColor || '');
        this.selectedParticipantChatBubbleTextColor.set(
          result.participantChatBubbleTextColor || ''
        );
        this.selectedClientSenderChatBubbleColor.set(result.clientSenderChatBubbleColor || '');
        this.selectedClientParticipantChatBubbleColor.set(
          result.clientParticipantChatBubbleColor || ''
        );
        this.selectedClientSenderChatBubbleTextColor.set(
          result.clientSenderChatBubbleTextColor || ''
        );
        this.selectedClientParticipantChatBubbleTextColor.set(
          result.clientParticipantChatBubbleTextColor || ''
        );
      }
    });
  }

  #setColorVariable(variable: string, value: string) {
    document.documentElement.style.setProperty(variable, value);
  }
}
